/* eslint-disable jsx-a11y/label-has-for */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import useComponent from '../../../../store/useComponent';
import RedactorEditor from '../RedactorEditor';

// The scopes are what Braze stores on the profile. The MAIN scope leads the array the front sends,
// and the backend uses scopes[0] to pick the SparkPost confirmation template and the
// origin_festival attribute — so it is a single field, not one more row in a list.
const initialState = {
  type: 'FanSaleSignup',
  mainScope: '',
  additionalScopes: ['scope_general'],
  title: { en: '', es: '', ca: '', pt: '' },
  text: { en: '', es: '', ca: '', pt: '' },
  buttonText: {
    en: 'Sign up to the Fan Sale',
    es: 'Apuntarme a la Fan Sale',
    ca: 'Apuntar-me a la Fan Sale',
    pt: 'Inscrever-me na Fan Sale',
  },
  disclaimer: {
    en: '*By signing up to the Fan Sale you are agreeing to receive information from Primavera Sound based on your interests, with news regarding their products and in accordance with the Privacy Policy.',
    es: '*Apuntándote a la Fan Sale estás aceptando recibir de Primavera Sound información elaborada a partir de tus intereses, con noticias relativas a sus productos y de conformidad con la Política de Privacidad.',
    ca: '*Apuntant-te a la Fan Sale estàs acceptant rebre de Primavera Sound informació elaborada a partir dels teus interessos, amb notícies relatives als seus productes i de conformitat amb la Política de Privacitat.',
    pt: '*Ao inscrever-te na Fan Sale, concordas em receber informações do Primavera Sound com base nos teus interesses, com novidades sobre os seus produtos e de acordo com a Política de Privacidade.',
  },
  pendingText: {
    en: 'Thank you, please confirm your email address in the email we have just sent you',
    es: 'Gracias, confirma tu correo en el email que te acabamos de enviar',
    ca: 'Gràcies, confirma el teu correu a l’email que t’acabem d’enviar',
    pt: 'Obrigado, confirma o teu e-mail na mensagem que acabámos de te enviar',
  },
  confirmedText: {
    en: 'Thank you! Your registration is confirmed',
    es: '¡Gracias! Te confirmamos que te has registrado',
    ca: 'Gràcies! Et confirmem que t’has registrat',
    pt: 'Obrigado! Confirmamos o teu registo',
  },
  ctaText: { en: '', es: '', ca: '', pt: '' },
  ctaLink: { en: '', es: '', ca: '', pt: '' },
  // Display switches. Off means the block renders exactly as it always has: left aligned, no
  // surface of its own. Pages that already store this component have neither key, and the front
  // reads a missing key as off.
  centered: false,
  whiteBackground: false,
};

const Checkbox = ({ label, name, checked, onChange, help }) => (
  <label htmlFor={name} className="flex items-start mb-4 cursor-pointer">
    <input
      className="mt-1 mr-3"
      name={name}
      id={name}
      type="checkbox"
      checked={checked === true}
      onChange={onChange}
    />
    <span className="block">
      <span className="block uppercase tracking-wide text-gray-700 text-xs font-bold">{label}</span>
      {help && <span className="block normal-case font-normal text-gray-600">{help}</span>}
    </span>
  </label>
);

Checkbox.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  help: PropTypes.string,
};

Checkbox.defaultProps = { checked: false, help: '' };

const LocalizedInput = ({ label, name, value, onChange, help }) => (
  <label htmlFor={name} className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
    {label}
    <input
      className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-1 leading-tight focus:outline-none focus:bg-white"
      name={name}
      type="text"
      onChange={onChange}
      value={value || ''}
    />
    {help && <span className="block normal-case font-normal text-gray-600 mb-3">{help}</span>}
  </label>
);

LocalizedInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  help: PropTypes.string,
};

LocalizedInput.defaultProps = { value: '', help: '' };

const FanSaleSignup = ({ activeLanguage, components, handlerSetComponents, id }) => {
  const { data, actions } = useComponent(initialState);
  const { initializeComponent, setFormValue, setLocalizedValue } = actions;

  const componentIndex = components.findIndex(obj => obj.id === id);
  const { component } = components[componentIndex];

  useEffect(() => {
    if (component) initializeComponent(component);
  }, []);

  useEffect(() => {
    handlerSetComponents({ id, component: data }, componentIndex);
  }, [data]);

  const handleLocalized = (e) => {
    const { name, value } = e.target;
    setLocalizedValue(name, value, activeLanguage);
  };

  const handleRedactor = name => (value) => setLocalizedValue(name, value, activeLanguage);

  // Stored as an array, edited as a comma-separated line — these are machine keys, not prose.
  // While the editor is typing, the field holds raw text — normalising on every keystroke
  // would eat the comma that separates two scopes. `null` means "show what is stored".
  const [scopesDraft, setScopesDraft] = useState(null);

  const scopesFieldValue = scopesDraft !== null
    ? scopesDraft
    : (data.additionalScopes || []).join(', ');

  const commitAdditionalScopes = () => {
    if (scopesDraft === null) return;
    setFormValue('additionalScopes', scopesDraft.split(',').map(scope => scope.trim()).filter(Boolean));
    setScopesDraft(null);
  };

  return (
    <div className="text-black w-full">
      <div className="flex w-full mb-4">
        <div className="w-1/2 pr-2">
          <label htmlFor="mainScope" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
            Main scope (required)
            <input
              className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-1 leading-tight focus:outline-none focus:bg-white"
              name="mainScope"
              type="text"
              placeholder="scope_presale26"
              onChange={e => setFormValue('mainScope', e.target.value.trim())}
              value={data.mainScope || ''}
            />
            <span className="block normal-case font-normal text-gray-600 mb-3">
              The campaign scope. It goes first in the list, and it is the one that picks the confirmation email template.
            </span>
          </label>
        </div>
        <div className="w-1/2 pl-2">
          <label htmlFor="additionalScopes" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
            Additional scopes
            <input
              className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-1 leading-tight focus:outline-none focus:bg-white"
              name="additionalScopes"
              type="text"
              placeholder="scope_general, scope_bcn"
              onChange={e => setScopesDraft(e.target.value)}
              onBlur={commitAdditionalScopes}
              value={scopesFieldValue}
            />
            <span className="block normal-case font-normal text-gray-600 mb-3">
              Comma separated. Everyone signing up gets these too.
            </span>
          </label>
        </div>
      </div>

      <LocalizedInput label="Title" name="title" value={(data.title || {})[activeLanguage]} onChange={handleLocalized} />

      <label htmlFor="text" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
        Body text
        <RedactorEditor text={(data.text || {})[activeLanguage] || ''} setText={handleRedactor('text')} id={`text${activeLanguage}${id}`} />
      </label>

      <LocalizedInput label="Button label" name="buttonText" value={(data.buttonText || {})[activeLanguage]} onChange={handleLocalized} />

      <label htmlFor="disclaimer" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
        Disclaimer
        <RedactorEditor text={(data.disclaimer || {})[activeLanguage] || ''} setText={handleRedactor('disclaimer')} id={`disc${activeLanguage}${id}`} />
      </label>

      <label htmlFor="pendingText" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
        “Confirm your email” screen
        <RedactorEditor text={(data.pendingText || {})[activeLanguage] || ''} setText={handleRedactor('pendingText')} id={`pend${activeLanguage}${id}`} />
      </label>

      <label htmlFor="confirmedText" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
        “You’re in” screen
        <RedactorEditor text={(data.confirmedText || {})[activeLanguage] || ''} setText={handleRedactor('confirmedText')} id={`conf${activeLanguage}${id}`} />
      </label>

      <div className="flex w-full mt-4">
        <div className="w-1/2 pr-2">
          <LocalizedInput
            label="CTA label (optional)"
            name="ctaText"
            value={(data.ctaText || {})[activeLanguage]}
            onChange={handleLocalized}
            help="Shown under the “you’re in” message. Leave empty for no button."
          />
        </div>
        <div className="w-1/2 pl-2">
          <LocalizedInput
            label="CTA link (optional)"
            name="ctaLink"
            value={(data.ctaLink || {})[activeLanguage]}
            onChange={handleLocalized}
            help="A path on this site, e.g. /residentes-bcn"
          />
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-300">
        <Checkbox
          label="Center content"
          name="centered"
          checked={data.centered}
          onChange={e => setFormValue('centered', e.target.checked)}
          help="Centres the block and its text in a 900px column instead of aligning it left."
        />
        <Checkbox
          label="White background"
          name="whiteBackground"
          checked={data.whiteBackground}
          onChange={e => setFormValue('whiteBackground', e.target.checked)}
          help="Paints a white band across the full width of the page behind this block."
        />
      </div>
    </div>
  );
};

FanSaleSignup.propTypes = {
  activeLanguage: PropTypes.oneOf(['en', 'es', 'ca', 'pt']).isRequired,
  components: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handlerSetComponents: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default FanSaleSignup;
